import { useMemo } from "react";
import { AlertCircle, Building2, Inbox, Loader2, Lock, Paperclip, RotateCcw, Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { OUTCOME_LABEL, type TaskOutcome } from "@/api/client360Tasks";
import type { DonePeriod, DoneTaskItem, DoneTaskListResponse, TaskScope } from "@/api/types";

/**
 * DONE-TASKS (TZ v1.1) — вміст вкладки «Виконані». Стан пошуку/періоду і сам
 * запит живуть у TasksListPage (scope-pill, allowedScopes, лічильник pill).
 * Backend-owned: сортування (dateCompleted ↓), keyset, total/doneTotal, дата і
 * місяць у TZ користувача (completedDate). FE нічого не рахує.
 */

const PERIODS: Array<{ k: DonePeriod; label: string }> = [
  { k: "all", label: "Увесь час" },
  { k: "90", label: "90 днів" },
  { k: "30", label: "30 днів" },
];

const MONTHS = [
  "Січень", "Лютий", "Березень", "Квітень", "Травень", "Червень",
  "Липень", "Серпень", "Вересень", "Жовтень", "Листопад", "Грудень",
];

interface Props {
  scope: TaskScope;
  pages: DoneTaskListResponse[] | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  search: string;
  onSearch: (v: string) => void;
  period: DonePeriod;
  onPeriod: (p: DonePeriod) => void;
  onResetFilters: () => void;
  onOpen: (id: string) => void;
  onGoTodo: () => void;
}

/** YYYY-MM-DD → ДД.ММ.РРРР */
function ddmmyyyy(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return y && m && d ? `${d}.${m}.${y}` : ymd;
}

/** «Ірина Коваль» → «Ірина К.» (D6). */
function shortName(full: string | null | undefined): string {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? "";
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
}

export function DoneTasksTab({
  scope,
  pages,
  isPending,
  isError,
  error,
  onRetry,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  search,
  onSearch,
  period,
  onPeriod,
  onResetFilters,
  onOpen,
  onGoTodo,
}: Props) {
  const first = pages?.[0];
  const items = useMemo(() => (pages ?? []).flatMap((p) => p.items), [pages]);
  const filterOn = search.trim() !== "" || period !== "all";

  // Заголовки місяців — лише візуальні (D6), за completedDate (TZ користувача).
  const groups = useMemo(() => {
    const out: Array<{ key: string; label: string; rows: DoneTaskItem[] }> = [];
    for (const t of items) {
      const [y, m] = (t.completedDate ?? "").split("-");
      const key = `${y}-${m}`;
      const last = out[out.length - 1];
      if (last && last.key === key) last.rows.push(t);
      else out.push({ key, label: `${MONTHS[Number(m) - 1] ?? ""} ${y}`, rows: [t] });
    }
    return out;
  }, [items]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor="done-search"
          className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm"
        >
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            id="done-search"
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Пошук за назвою задачі або клієнтом"
            aria-label="Пошук виконаних задач"
            className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          />
        </label>
        <div role="group" aria-label="Період" className="inline-flex rounded-lg border bg-card p-1">
          {PERIODS.map((p) => (
            <button
              key={p.k}
              type="button"
              onClick={() => onPeriod(p.k)}
              aria-pressed={period === p.k}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                period === p.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="space-y-2" data-testid="done-skeleton">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : isError ? (
        <div className="rounded-lg border bg-destructive/5 p-6 text-center">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" aria-hidden />
          <p className="text-sm text-destructive">Не вдалося завантажити</p>
          {error instanceof Error && error.message && (
            <p className="mt-1 text-xs text-muted-foreground">{error.message}</p>
          )}
          <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={onRetry}>
            <RotateCcw className="h-3.5 w-3.5" /> Спробувати ще
          </Button>
        </div>
      ) : (first?.doneTotal ?? 0) === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center" data-testid="done-empty">
          <p className="text-sm font-medium">
            {scope === "team" ? "У команди ще немає виконаних задач" : "Виконаних задач поки немає"}
          </p>
          <Button size="sm" variant="outline" className="mt-3" onClick={onGoTodo}>
            До відкритих задач
          </Button>
        </div>
      ) : items.length === 0 ? (
        <>
          {filterOn && (
            <p className="text-xs text-muted-foreground tabular-nums" data-testid="done-found">Знайдено 0</p>
          )}
          <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center" data-testid="done-noresults">
            <p className="text-sm font-medium">Нічого не знайдено</p>
            <p className="mt-1 text-xs text-muted-foreground">Змініть запит або період.</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={onResetFilters}>
              Скинути фільтри
            </Button>
          </div>
        </>
      ) : (
        <>
          {filterOn && (
            <p className="text-xs text-muted-foreground tabular-nums" data-testid="done-found">
              Знайдено {first?.total ?? 0}
            </p>
          )}
          {groups.map((g) => (
            <section key={g.key} data-testid="done-month">
              <h3 className="mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {g.label}
              </h3>
              <div className="space-y-1.5">
                {g.rows.map((t) => (
                  <DoneTaskRow key={t.id} task={t} showAssignee={scope !== "my"} onOpen={() => onOpen(t.id)} />
                ))}
              </div>
            </section>
          ))}
          {hasNextPage && (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
              data-testid="done-more"
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed py-2.5 text-sm text-foreground hover:bg-muted/40 disabled:opacity-60"
            >
              {isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Показати ще
            </button>
          )}
        </>
      )}
    </div>
  );
}

function DoneTaskRow({ task, showAssignee, onOpen }: { task: DoneTaskItem; showAssignee: boolean; onOpen: () => void }) {
  const client = task.holdingName ?? task.accountName;
  const outcome = task.outcome ? (OUTCOME_LABEL[task.outcome as TaskOutcome] ?? task.outcome) : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      data-testid="done-row"
      data-task-id={task.id}
      className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border bg-background px-3.5 py-3 transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span aria-hidden className="grid h-4.5 w-4.5 shrink-0 place-items-center rounded-full bg-primary">
        <svg viewBox="0 0 16 16" className="h-2.5 w-2.5 text-primary-foreground" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8.5l3 3 7-7" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{task.title}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {task.clientAccessLocked ? (
            <span className="inline-flex items-center gap-1 italic" data-testid="done-client-locked">
              <Lock className="h-3 w-3" aria-hidden /> Клієнт приховано
            </span>
          ) : client ? (
            <span className="inline-flex min-w-0 items-center gap-1" data-testid="done-client">
              <Building2 className="h-3 w-3 shrink-0" aria-hidden />
              <span className="max-w-[220px] truncate">{client}</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 italic" data-testid="done-client-general">
              <Inbox className="h-3 w-3" aria-hidden /> Загальна
            </span>
          )}
          {showAssignee && task.assigneeName && (
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" aria-hidden /> виконавець: {shortName(task.assigneeName)}
            </span>
          )}
          {task.originKind === "meeting_report" ? (
            <span data-testid="done-origin">зі звіту зустрічі</span>
          ) : task.originKind === "system" ? (
            <span data-testid="done-origin">створено автоматично</span>
          ) : task.createdByName ? (
            <span data-testid="done-origin">поставив: {shortName(task.createdByName)}</span>
          ) : null}
          {(task.attachmentCount ?? 0) > 0 && (
            <span className="inline-flex items-center gap-0.5 tabular-nums" aria-label={`Вкладень: ${task.attachmentCount}`}>
              <Paperclip className="h-3 w-3" aria-hidden /> {task.attachmentCount}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-muted-foreground max-sm:w-full max-sm:flex-row max-sm:items-center max-sm:justify-between max-sm:pl-7">
        {outcome && (
          <span className="rounded-full bg-success px-2 py-0.5 text-[11.5px] font-medium text-success-foreground">
            {outcome}
          </span>
        )}
        <span className="tabular-nums">виконано {ddmmyyyy(task.completedDate)}</span>
      </div>
    </div>
  );
}
